'use client';

import type { LinkProps } from '@mui/material/Link';
import { paths } from 'src/routes/paths';

import { useId } from 'react';
import { mergeClasses } from 'minimal-shared/utils';

import Link from '@mui/material/Link';
import { styled, useTheme } from '@mui/material/styles';

import { RouterLink } from 'src/routes/components';

import { logoClasses } from './classes';

// ----------------------------------------------------------------------

export type LogoProps = LinkProps & {
  isSingle?: boolean;
  disabled?: boolean;
};

export function Logo({
  sx,
  disabled,
  className,
  href = paths.dashboard.root,
  isSingle = true,
  ...other
}: LogoProps) {
  const theme = useTheme();

  const uniqueId = useId();

  const PRIMARY_LIGHT = theme.palette.primary.light;
  const PRIMARY_MAIN = theme.palette.primary.main;
  const SECONDARY_MAIN = theme.palette.secondary.main;

  // Ordmärket "Flinky" — gradient från primary.light via primary.main till secondary.main.
  const wordmark = (
    <svg
      width="100%"
      height="100%"
      viewBox="0 0 270 80"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <defs>
        <linearGradient id={uniqueId} x1="0" y1="0" x2="1" y2="0">
          <stop stopColor={PRIMARY_LIGHT} />
          <stop offset="0.45" stopColor={PRIMARY_MAIN} />
          <stop offset="1" stopColor={SECONDARY_MAIN} />
        </linearGradient>
      </defs>
      <text
        x="135"
        y="62"
        textAnchor="middle"
        fontSize="72"
        fontWeight="800"
        letterSpacing="-2"
        fill={`url(#${uniqueId})`}
        style={{ fontFamily: theme.typography.fontFamily }}
      >
        Flinky
      </text>
    </svg>
  );

  return (
    <LogoRoot
      component={RouterLink}
      href={href}
      aria-label="Logo"
      underline="none"
      className={mergeClasses([logoClasses.root, className])}
      sx={[
        {
          width: 72,
          height: 40,
          ...(!isSingle && { width: 120, height: 40 }),
          ...(disabled && { pointerEvents: 'none' }),
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
      {...other}
    >
      {wordmark}
    </LogoRoot>
  );
}

// ----------------------------------------------------------------------

const LogoRoot = styled(Link)(() => ({
  flexShrink: 0,
  color: 'transparent',
  display: 'inline-flex',
  verticalAlign: 'middle',
}));
